import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import departmentService, {
  Department,
  DepartmentFilters,
  CreateDepartmentPayload,
} from '@/services/department.service';

export const DEPARTMENTS_QUERY_KEY = 'departments';
export const DEPARTMENTS_SELECTION_QUERY_KEY = 'departments-selection';

export function useDepartments(filters?: DepartmentFilters) {
  return useQuery({
    queryKey: [DEPARTMENTS_QUERY_KEY, filters],
    queryFn: () => departmentService.list(filters),
    staleTime: 30000,
  });
}

export function useDepartment(id: string | undefined) {
  return useQuery({
    queryKey: [DEPARTMENTS_QUERY_KEY, id],
    queryFn: () => departmentService.getById(id!),
    enabled: !!id,
    staleTime: 30000,
  });
}

export function useDepartmentsForSelection() {
  return useQuery({
    queryKey: [DEPARTMENTS_SELECTION_QUERY_KEY],
    queryFn: () => departmentService.getForSelection(),
    staleTime: 60000,
  });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (payload: CreateDepartmentPayload) => departmentService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_SELECTION_QUERY_KEY] });
      message.success('Department created');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to create department');
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateDepartmentPayload> }) =>
      departmentService.update(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY, id] });
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_SELECTION_QUERY_KEY] });
      message.success('Department updated');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to update department');
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id: string) => departmentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_SELECTION_QUERY_KEY] });
      message.success('Department deleted');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.message || error?.message || 'Failed to delete department');
    },
  });
}
